// src/pages/MyRankingDetail.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios, { PUBLIC_BASE } from '../utils/axiosInstance';

// Fallback to ObjectId timestamp when createdAt/updatedAt are missing
function oidMs(id) {
  if (!id || typeof id !== 'string' || id.length < 8) return 0;
  const sec = parseInt(id.slice(0, 8), 16);
  return Number.isFinite(sec) ? sec * 1000 : 0;
}
function recTimeMs(r) {
  const t = Date.parse(r?.updatedAt || r?.createdAt || '');
  if (Number.isFinite(t)) return t;
  return oidMs(r?._id);
}

// normalize a DB image value to URL
const toImageUrl = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string' && /^https?:\/\//i.test(raw)) return raw;
  const s = String(raw);
  const path = s.startsWith('/') ? s : `/${s}`;
  return `${PUBLIC_BASE}${path}`;
};

/* ---------- Inline card used for “similar users” rows ---------- */
const SimilarUserRow = ({ user }) => {
  const [status, setStatus] = useState(user.status); // 'friend' | 'outgoing' | 'incoming' | 'none'
  const [busy, setBusy] = useState(false);

  const img = toImageUrl(user.image);

  const requestFriend = async () => {
    try {
      setBusy(true);
      await axios.post(`/friends/requests/${encodeURIComponent(user.username)}`);
      setStatus('outgoing');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card" style={{ padding: 12, width: '100%' }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, minWidth:0 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
          <div style={{ width:36, height:36, borderRadius:8, overflow:'hidden', background:'#eef0f2', display:'flex', alignItems:'center', justifyContent:'center' }}>
            {img ? <img src={img} alt={user.username} style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <span style={{ color:'#7a7a7a', fontSize:12 }}>No img</span>}
          </div>
          <div style={{ display:'flex', flexDirection:'column' }}>
            <span>@{user.username}</span>
            <span style={{ fontSize:12, color:'#6b7280' }}>Similarity: <strong>{user.similarity}%</strong></span>
          </div>
        </div>
        {status === 'friend' ? (
          <button className="btn" disabled>Friends</button>
        ) : status === 'outgoing' || status === 'incoming' ? (
          <button className="btn" disabled>Pending</button>
        ) : (
          <button className="btn btn-primary" onClick={requestFriend} disabled={busy}>
            {busy ? 'Adding…' : 'Add Friend'}
          </button>
        )}
      </div>
    </div>
  );
};

const MyRankingDetail = () => {
  const { listId } = useParams();
  const username = localStorage.getItem('username');
  const navigate = useNavigate();

  const [lists, setLists] = useState([]);
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let alive = true;
    const run = async () => {
      try {
        setErr('');
        setLoading(true);
        // keep your original endpoints intact
        const [lRes, rRes] = await Promise.all([
          axios.get('/rankings'),
          axios.get(`/rankings/user/${username}`),
        ]);
        if (!alive) return;
        setLists(lRes.data || []);
        setMine(rRes.data || []);
      } catch (e) {
        console.error('detail load error', e);
        if (alive) setErr('Failed to load ranking.');
      } finally {
        if (alive) setLoading(false);
      }
    };
    if (username) run();
    return () => {
      alive = false;
    };
  }, [username]);

  const listMeta = useMemo(
    () => (lists || []).find((l) => String(l._id) === String(listId)),
    [lists, listId]
  );

  // Pick the newest ranking for this list (robust even if timestamps were missing before)
  const rankingRec = useMemo(() => {
    const set = (mine || []).filter((r) => String(r.listId) === String(listId));
    set.sort((a, b) => recTimeMs(b) - recTimeMs(a));
    return set[0] || null;
  }, [mine, listId]);

  const order = rankingRec?.ranking || [];

  // ===== Similar users (toggle + fetch) =====
  const [simOpen, setSimOpen] = useState(false);
  const [simLoading, setSimLoading] = useState(false);
  const [simUsers, setSimUsers] = useState([]);

  const toggleSimilar = async () => {
    if (!simOpen && simUsers.length === 0) {
      try {
        setSimLoading(true);
        // ask for 90+ explicitly (backend also defaults to 90)
        const res = await axios.get(`/users/me/rankings/${listId}/similar-users?limit=8&min=90`);
        setSimUsers(res.data?.users || []);
      } finally {
        setSimLoading(false);
      }
    }
    setSimOpen((v) => !v);
  };

  return (
    <div className="scroll-shell">
      <div className="scroll-body">
        <button className="logout-btn" onClick={() => navigate(-1)} style={{ marginBottom: 16 }}>
          ← Back
        </button>

        {loading && <div style={{ color: '#666' }}>Loading…</div>}
        {err && <div style={{ color: '#b91c1c' }}>{err}</div>}

        {listMeta && (
          <div className="rank-detail">
            <h2 className="rank-detail-title">{listMeta.listName}</h2>

            {order.length > 0 ? (
              <ol className="rank-order">
                {order.map((label, idx) => (
                  <li key={idx}>
                    <span className="rank-chip">{idx + 1}</span>
                    <span className="rank-label">{label}</span>
                  </li>
                ))}
              </ol>
            ) : (
              <div style={{ color: '#6b7280', marginTop: 8 }}>
                You haven’t ranked this list yet.
              </div>
            )}

            {/* Actions row: keep Re-rank (left), toggle similar (right) */}
            <div
              className="rank-actions"
              style={{ display: 'flex', justifyContent: 'space-between', gap: 12, width: 'min(560px, 100%)', margin: '16px auto 0' }}
            >
              <button
                className="logout-btn"
                onClick={() => navigate(`/lists/${listId}`)}
                style={{ borderColor: '#2563eb', background: '#dbeafe', color: '#1e3a8a' }}
              >
                Re-rank this list
              </button>

              <button className="btn" onClick={toggleSimilar} style={{ borderColor: '#0e0e0e', background: '#fff' }}>
                {simOpen ? 'Hide all users with a similar ranking' : 'Show all users with a similar ranking'}
              </button>
            </div>

            {simOpen && (
              <div style={{ maxWidth: 560, margin: '14px auto 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
                {simLoading ? (
                  <div style={{ textAlign: 'center', color: '#777' }}>Loading…</div>
                ) : simUsers.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#777' }}>No similar users yet.</div>
                ) : (
                  simUsers.map((u) => <SimilarUserRow key={u.username} user={{ ...u }} />)
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRankingDetail;
